// src/utils/messages.tsx

import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

export type MessageType = 'success' | 'error';

export interface AppMessage {
  type: MessageType;
  text: string;
}

export type MessageSetter = (msg: AppMessage | null) => void;

/**
 * Shows a message and clears it after a timeout.
 * If persist is true, stores the message in sessionStorage so it persists across navigation.
 */
export function showTempMessage(
  setMessage: MessageSetter,
  message: AppMessage,
  timeoutMs = 3000,
  persist = false
) {
  setMessage(message);
  
  // Store in sessionStorage if persist is true (for cross-page messages)
  if (persist && typeof window !== 'undefined') {
    sessionStorage.setItem('pendingToastMessage', JSON.stringify(message));
  }
  
  if (timeoutMs > 0) {
    setTimeout(() => {
      setMessage(null);
      if (persist && typeof window !== 'undefined') {
        sessionStorage.removeItem('pendingToastMessage');
      }
    }, timeoutMs);
  }
}

/**
 * Checks for and displays any pending toast message from sessionStorage.
 * Call this in useEffect on component mount to show messages after navigation.
 */
export function checkPendingMessage(setMessage: MessageSetter) {
  if (typeof window === 'undefined') return;
  
  const pendingMessage = sessionStorage.getItem('pendingToastMessage');
  if (pendingMessage) {
    try {
      const message: AppMessage = JSON.parse(pendingMessage);
      setMessage(message);
      sessionStorage.removeItem('pendingToastMessage');
      // Auto-clear after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error parsing pending toast message:', error);
      sessionStorage.removeItem('pendingToastMessage');
    }
  }
}

/**
 * Hook for managing toast messages.
 * Returns the message state, setter, and a convenience function to show messages.
 */
export function useToast() {
  const [message, setMessage] = useState<AppMessage | null>(null);

  const showMessage = useCallback((msg: AppMessage, timeoutMs = 3000) => {
    showTempMessage(setMessage, msg, timeoutMs);
  }, []);

  return {
    message,
    setMessage,
    showMessage
  };
}

/**
 * Toast notification component that displays messages in the top-right corner.
 * Use with the useToast hook for easy message management.
 */
export const Toast: React.FC<{ message: AppMessage | null }> = ({ message }) => {
  useEffect(() => {
    if (message) {
      console.log('Toast message:', message);
    }
  }, [message]);

  if (!message) return null;

  // Use portal to render at document body level to ensure it's above all content
  const toastElement = (
    <div 
      className={`toast-notification toast-${message.type}`}
    >
      {message.text}
    </div>
  );

  // Only use portal if document.body exists (client-side)
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(toastElement, document.body);
  }

  return toastElement;
};

